import { useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { ApiClientError } from '@/api/client'
import { completePasswordReset } from '@/api/vendorsApi'

import styles from './AuthPages.module.css'

/** Public page to set a password from an admin invite / reset link. */
export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = useMemo(() => (params.get('token') || '').trim(), [params])
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!token) {
      setError('This reset link is missing its token. Ask an admin to send a new one.')
      return
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await completePasswordReset(token, password)
      setDone(true)
      window.setTimeout(() => navigate('/login'), 1800)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not reset password.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles.page}>
      <div className={styles.panel}>
        <p className={styles.brand}>
          Oikos <span>Market</span>
        </p>
        <h1 className={styles.title}>Set your password</h1>
        <p className={styles.lead}>
          Choose a password for your account, then sign in.
        </p>

        {done ? (
          <p className={styles.hint}>Password saved. Taking you to sign in…</p>
        ) : (
          <form className={styles.form} onSubmit={(event) => void onSubmit(event)}>
            <label className={styles.label}>
              New password
              <input
                className={styles.input}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                required
                autoComplete="new-password"
              />
            </label>
            <label className={styles.label}>
              Confirm password
              <input
                className={styles.input}
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={6}
                required
                autoComplete="new-password"
              />
            </label>
            {error ? <p className={styles.error}>{error}</p> : null}
            <button className={styles.submit} type="submit" disabled={busy || !token}>
              {busy ? 'Saving…' : 'Save password'}
            </button>
          </form>
        )}

        <p className={styles.footer}>
          <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </section>
  )
}
